import { MpxResponseError } from '@cibo/core'
import { Form, Markdown, SubmitButton } from '@cibo/ui'
import ReplayRoundedIcon from '@mui/icons-material/ReplayRounded'
import { Button, Stack } from '@mui/material'
import { Field } from 'formik'
import { TextField } from 'formik-mui'
import { FC, useCallback, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import * as Yup from 'yup'

import { InputLabel } from '@mui/material'
import { HttpErrorDisplay } from '../HttpErrorDisplay'

type Props = {
  code?: string
  registrationError?: MpxResponseError
  contactType?: any
  contactAddress?: any
  formAuthToken?: string
  hideMessage?: boolean
  initialValues?: any
  isLoading?: boolean
  onResendCode?: () => void
  onStartOver?: () => void
  onSubmit(values: any): void
  submitLabel?: string
}

const VerifyCodeForm: FC<Props> = ({
  children,
  code,
  registrationError,
  contactType,
  contactAddress,
  formAuthToken,
  hideMessage,
  initialValues,
  isLoading,
  onResendCode,
  onStartOver,
  onSubmit,
  submitLabel,
}) => {
  const { t } = useTranslation('auth')

  const handleSubmit = ({ verifyCode }: { verifyCode: string }) => {
    sendVerifyCode(verifyCode.replace(/\s+/g, ''))
  }

  const sendVerifyCode = useCallback(
    (verifyCode: string) => {
      const values = {
        ...initialValues,
        verifyCode: verifyCode.toUpperCase(),
        contactAddress,
        contactType,
      }
      if (formAuthToken) {
        values.formAuthToken = formAuthToken
      }

      onSubmit(values)
    },
    [code, formAuthToken, contactAddress, contactType]
  )

  // auto-submit if we have values
  useEffect(() => {
    if (formAuthToken && contactAddress && contactType && code) {
      sendVerifyCode(code)
    }
  }, [code])

  /**
   * There is probably a better way to do this.
   * The validation schema never changes, but we need access to the react-i18next
   * context to localize the error messages; hence this ephemeral validation schema
   */
  const RegistrationSchema = Yup.object().shape({
    verifyCode: Yup.string().required(t('verifyCode')),
  })

  return (
    <Form
      validationSchema={RegistrationSchema}
      enableReinitialize
      initialValues={
        initialValues || {
          formAuthToken,
          contactType,
          contactAddress,
          verifyCode: code || '',
        }
      }
      onSubmit={handleSubmit}
    >
      <Stack spacing={4} sx={{ wordBreak: 'break-word' }}>
        {!!!hideMessage && (
          <Markdown>{t('verifyInstructions', { email: contactAddress })}</Markdown>
        )}

        <Stack>
          <InputLabel shrink htmlFor="verifyCode">
            {t('verifyCode')}
          </InputLabel>
          <Field
            id="verifyCode"
            name="verifyCode"
            component={TextField}
            disabled={isLoading}
            autoComplete="off"
            InputProps={{
              inputProps: { 'data-testid': 'verify_code', style: { textTransform: 'uppercase' } },
            }}
          />
          <HttpErrorDisplay
            error={registrationError}
            defaultMessage={t('error')}
            statusMap={{ 400: t('verifyWrongCode') }}
          />
        </Stack>

        {children}

        <Stack direction="row" justifyContent={'space-between'}>
          {onStartOver && <Button onClick={onStartOver}>{t('startOver')}</Button>}
          {onResendCode && (
            <Button size="small" startIcon={<ReplayRoundedIcon />} onClick={onResendCode}>
              {t('resendCode')}
            </Button>
          )}
          <SubmitButton
            loading={isLoading}
            title={submitLabel || t('verify')}
            testId="verify_button"
          />
        </Stack>
      </Stack>
    </Form>
  )
}

export default VerifyCodeForm
